import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  hexToRgb,
  Stack,
  Typography
} from '@mui/material'
import { useMemo } from 'react'
import Carousel from 'react-material-ui-carousel'

import { InspectionStatusWithEntries } from '../../models/inspections'
import { InspectionEntryStatusChip } from './InspectionEntryStatusChip'

interface Props {
  status: InspectionStatusWithEntries
}

const PassedEntriesList = ({ status }: Props) => {
  const passedEntriesWithInfo = useMemo(() => {
    return status.entries.filter(
      (e) => e.prompts.find((p) => p.media.length > 0) || e.info?.customerNotes
    )
  }, [status.entries])

  const passedEntriesWithoutInfo = useMemo(() => {
    return status.entries.filter(
      (e) => !passedEntriesWithInfo.find((p) => p.id === e.id)
    )
  }, [status.entries, passedEntriesWithInfo])

  return (
    <Accordion
      defaultExpanded={true}
      variant="outlined"
      disableGutters
      sx={{ borderRadius: '4px' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="h5"
          color={hexToRgb('#' + status.info!.color!)}
          width="90%">
          {`${status.entries.length} ${status.info!.title}`}
        </Typography>
        <InspectionEntryStatusChip status={status} />
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Accordion
            defaultExpanded={false}
            variant="outlined"
            disableGutters
            sx={{ borderRadius: '4px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h4" width="100%">
                Passed
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {passedEntriesWithoutInfo.map((entry, index) => (
                <Stack key={index}>
                  <Stack direction="row" alignItems="center">
                    <CheckCircleOutlineIcon color="success" />
                    <Typography>{entry.info!.title}</Typography>
                  </Stack>
                </Stack>
              ))}
            </AccordionDetails>
          </Accordion>
          <Accordion
            defaultExpanded={true}
            variant="outlined"
            disableGutters
            sx={{ borderRadius: '4px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h4" width="100%">
                Passed with Notes and/or Photos
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {passedEntriesWithInfo.map((entry, index) => (
                <Stack key={index}>
                  <Stack direction="row" alignItems="center">
                    <CheckCircleOutlineIcon color="success" />
                    <Typography>{entry.info!.title}</Typography>
                  </Stack>
                  {entry.info!.customerNotes && (
                    <Typography>{`Noted: ${
                      entry.info!.customerNotes
                    }`}</Typography>
                  )}
                  {entry.prompts.flatMap((p) => p.media).length > 0 && (
                    <Carousel
                      height="300px"
                      autoPlay={false}
                      navButtonsAlwaysVisible>
                      {entry.prompts
                        .flatMap((p) => p.media)
                        .map((media) => (
                          <ButtonBase
                            key={media.id}
                            onClick={() => window.open(media.mediaUrl)}>
                            <Box
                              component="img"
                              sx={{ maxWidth: '100%' }}
                              src={media.thumbnailUrl}
                              alt={media.info?.description}
                            />
                          </ButtonBase>
                        ))}
                    </Carousel>
                  )}
                </Stack>
              ))}
            </AccordionDetails>
          </Accordion>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default PassedEntriesList
